import { LanguageType, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Translation } from '../../domain/translation/Translation';
import { TranslationDTO } from '../../domain/translation/TranslationDTO';
import { TranslationDomain } from '../../domain/translation/TranslationDomain';
import { TranslationMapper } from '../../mappers/translation/TranslationMapper';
import { SessionRepository } from '../session/SessionRepository';
import { TranslationsRepository } from './TranslationsRepository';

export class TranslationsRepositoryApi implements TranslationsRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}
  async getTranslations({
    languages,
    domain = `planner`,
  }: {
    languages: LanguageType[];
    domain?: TranslationDomain;
  }): Promise<{ language: LanguageType; translations: Record<string, string> }[]> {
    const requests = languages.map((language) => {
      return {
        method: this.requests.get.bind(this.requests),
        params: {
          uri: `/locale`,
          params: {
            language,
            domain,
          },
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    return response.map((translations, index) => ({
      language: languages[index],
      translations,
    }));
  }

  async search({
    language,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    language: LanguageType;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Translation>> {
    const response = await this.requests.post({
      uri: `/locale/search?language=${language}`,
      data: {
        offset,
        limit,
        predicates: predicates.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<TranslationDTO>;

    return {
      ...data,
      docs: data.docs.map(TranslationMapper.toDomain),
    };
  }

  async createTranslation({
    language,
    key,
    value,
    domain,
  }: {
    language: LanguageType;
    key: string;
    value: string;
    domain: TranslationDomain;
  }): Promise<void> {
    await this.requests.post({
      uri: `/locale?language=${language}&domain=${domain}`,
      data: {
        key,
        value,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateTranslation({
    language,
    key,
    value,
    domain,
  }: {
    language: LanguageType;
    key: string;
    value: string;
    domain: TranslationDomain;
  }): Promise<void> {
    await this.requests.put({
      uri: `/locale?language=${language}&domain=${domain}`,
      data: {
        key,
        value,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async deleteTranslation({ key, domain }: { key: string; domain: TranslationDomain }): Promise<void> {
    await this.requests.delete({
      uri: `/locale?domain=${domain}&key=${key}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }
}
